import { memo, useMemo } from 'react'
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react'
import { GitCommitHorizontal } from 'lucide-react'
import '@xyflow/react/dist/style.css'
import type { GitHistoryCommit, GitHistoryProviderBranch } from '../../lib/api'
import { formatRelativeTime } from '../../lib/format'

const LANE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6']
const NODE_WIDTH = 320
const LANE_WIDTH = 420
const ROW_HEIGHT = 184

interface GitHistoryGraphProps {
  commits: GitHistoryCommit[]
  providerBranchHeads: Map<string, GitHistoryProviderBranch[]>
  selectedSha: string | null
  lastSeenSha?: string | null
  lastDeployedSha?: string | null
  onSelectSha: (sha: string) => void
}

interface GitCommitNodeData extends Record<string, unknown> {
  subject: string
  shortSha: string
  authoredLabel: string
  authorName: string
  lane: number
  decorations: string[]
  providerBranches: GitHistoryProviderBranch[]
  isSelected: boolean
  isDeployed: boolean
  isSeen: boolean
}

type GitCommitFlowNode = Node<GitCommitNodeData, 'commit'>

function laneColor(lane: number): string {
  return LANE_COLORS[Math.abs(lane) % LANE_COLORS.length]
}

function graphLane(graph: string): number {
  const starIndex = graph.indexOf('*')
  if (starIndex < 0) return 0
  return Math.max(0, Math.floor(starIndex / 2))
}

function refBadgeClass(label: string): string {
  if (label.startsWith('HEAD')) return 'border-blue-500/30 bg-blue-500/10 text-blue-500'
  if (label.startsWith('tag:')) return 'border-amber-500/30 bg-amber-500/10 text-amber-500'
  if (label.startsWith('origin/') || label.includes('/')) return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
  return 'border-border bg-secondary text-muted-foreground'
}

function providerBranchBadgeClass(branch: GitHistoryProviderBranch): string {
  if (branch.default) return 'border-blue-500/30 bg-blue-500/10 text-blue-500'
  if (branch.protected) return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
  return 'border-border bg-secondary text-muted-foreground'
}

const GitCommitNode = memo(function GitCommitNode({ data, selected }: NodeProps<GitCommitFlowNode>) {
  const color = laneColor(data.lane)
  const displayedDecorations = data.decorations.slice(0, 2)
  const hiddenDecorationCount = Math.max(0, data.decorations.length - displayedDecorations.length)
  const displayedProviderBranches = data.providerBranches.slice(0, 2)
  const hiddenProviderCount = Math.max(0, data.providerBranches.length - displayedProviderBranches.length)

  return (
    <div
      className={`min-h-[136px] w-[320px] rounded-lg border bg-white p-3 text-zinc-950 shadow-lg shadow-zinc-950/5 backdrop-blur-sm transition-colors dark:bg-zinc-900 dark:text-zinc-100 dark:shadow-black/30 ${
        selected || data.isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-zinc-200 dark:border-zinc-700'
      }`}
      style={{ width: NODE_WIDTH }}
    >
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="!h-3 !w-3 !border-2 !border-white dark:!border-zinc-900"
        style={{ backgroundColor: color, top: -6 }}
      />

      <div className="flex items-start gap-3">
        <div className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-[13px] font-semibold leading-5">{data.subject || '(no subject)'}</p>
            <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-border px-2 py-1 font-mono text-[10px] text-muted-foreground">
              <GitCommitHorizontal className="h-3 w-3" />
              {data.shortSha}
            </span>
          </div>
          <p className="mt-1 truncate text-[11px] text-muted-foreground">
            {data.authorName} • {data.authoredLabel}
          </p>
        </div>
      </div>

      {(displayedDecorations.length > 0 || displayedProviderBranches.length > 0 || data.isDeployed || data.isSeen) && (
        <div className="mt-3 flex flex-wrap gap-1">
          {displayedDecorations.map((decoration) => (
            <span
              key={decoration}
              className={`inline-flex max-w-[230px] truncate rounded-full border px-2 py-0.5 text-[10px] ${refBadgeClass(decoration)}`}
            >
              {decoration}
            </span>
          ))}
          {hiddenDecorationCount > 0 && (
            <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
              +{hiddenDecorationCount} refs
            </span>
          )}
          {displayedProviderBranches.map((branch) => (
            <span
              key={branch.name}
              className={`inline-flex max-w-[230px] truncate rounded-full border px-2 py-0.5 text-[10px] ${providerBranchBadgeClass(branch)}`}
            >
              remote:{branch.name}
            </span>
          ))}
          {hiddenProviderCount > 0 && (
            <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
              +{hiddenProviderCount} remote
            </span>
          )}
          {data.isDeployed && (
            <span className="rounded-full border border-green-500/30 bg-green-500/10 px-2 py-0.5 text-[10px] text-green-500">
              Deployed
            </span>
          )}
          {data.isSeen && !data.isDeployed && (
            <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-500">
              Linked HEAD
            </span>
          )}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="!h-3 !w-3 !border-2 !border-white dark:!border-zinc-900"
        style={{ backgroundColor: color, bottom: -6 }}
      />
    </div>
  )
})

const nodeTypes = { commit: GitCommitNode }

export default function GitHistoryGraph({
  commits,
  providerBranchHeads,
  selectedSha,
  lastSeenSha = null,
  lastDeployedSha = null,
  onSelectSha,
}: GitHistoryGraphProps) {
  const { nodes, edges } = useMemo(() => {
    const nodes: GitCommitFlowNode[] = []
    const edges: Edge[] = []
    const commitMap = new Map(commits.map((commit) => [commit.sha, commit]))
    const laneBySha = new Map<string, number>()

    for (const commit of commits) {
      laneBySha.set(commit.sha, graphLane(commit.graph || '*'))
    }

    commits.forEach((commit, index) => {
      const lane = laneBySha.get(commit.sha) || 0
      nodes.push({
        id: commit.sha,
        type: 'commit',
        draggable: false,
        selectable: true,
        position: {
          x: lane * LANE_WIDTH,
          y: index * ROW_HEIGHT,
        },
        data: {
          subject: commit.subject,
          shortSha: commit.shortSha,
          authoredLabel: formatRelativeTime(commit.authoredAt),
          authorName: commit.authorName,
          lane,
          decorations: commit.decorations,
          providerBranches: providerBranchHeads.get(commit.sha) || [],
          isSelected: selectedSha === commit.sha,
          isDeployed: lastDeployedSha === commit.sha,
          isSeen: lastSeenSha === commit.sha,
        },
      })

      commit.parents.forEach((parent, parentIndex) => {
        if (!commitMap.has(parent)) return
        const parentLane = laneBySha.get(parent) || lane
        edges.push({
          id: `${commit.sha}-${parent}-${parentIndex}`,
          source: commit.sha,
          target: parent,
          type: 'smoothstep',
          animated: false,
          style: {
            stroke: laneColor(parentLane),
            strokeWidth: commit.parents.length > 1 ? 2.4 : 2,
          },
        })
      })
    })

    return { nodes, edges }
  }, [commits, lastDeployedSha, lastSeenSha, providerBranchHeads, selectedSha])

  return (
    <>
      {/* Mobile (< sm): simple vertical list view — the ReactFlow canvas is unusable
          on small touch screens (D-32, D-33 / UX-18). */}
      <div className="block sm:hidden max-h-[440px] space-y-2 overflow-y-auto rounded-lg border border-zinc-200 bg-white p-2 dark:border-zinc-800 dark:bg-zinc-950">
        {commits.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No commits yet.</p>
        ) : (
          commits.map((commit) => {
            const isSelected = selectedSha === commit.sha
            const isDeployed = lastDeployedSha === commit.sha
            const isSeen = lastSeenSha === commit.sha
            const branches = providerBranchHeads.get(commit.sha) ?? []
            return (
              <button
                key={commit.sha}
                type="button"
                onClick={() => onSelectSha(commit.sha)}
                className={`flex w-full items-start gap-2 rounded-md border p-2 text-left text-sm transition-colors ${
                  isSelected
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-zinc-200 bg-white hover:bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-950 dark:hover:bg-zinc-900'
                }`}
              >
                <GitCommitHorizontal className="mt-0.5 h-4 w-4 flex-shrink-0 text-zinc-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{commit.shortSha}</span>
                    {isDeployed && (
                      <span className="rounded-full border border-green-500/30 bg-green-500/10 px-1.5 py-0.5 text-[10px] text-green-500">
                        Deployed
                      </span>
                    )}
                    {isSeen && !isDeployed && (
                      <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] text-blue-500">
                        Linked HEAD
                      </span>
                    )}
                    {branches.slice(0, 2).map((b) => (
                      <span
                        key={b.name}
                        className={`rounded-full border px-1.5 py-0.5 text-[10px] ${providerBranchBadgeClass(b)}`}
                      >
                        remote:{b.name}
                      </span>
                    ))}
                    {branches.length > 2 && (
                      <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        +{branches.length - 2} remote
                      </span>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-2 break-words">{commit.subject || '(no subject)'}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {commit.authorName} · {formatRelativeTime(commit.authoredAt)}
                  </p>
                </div>
              </button>
            )
          })
        )}
      </div>

      {/* Desktop (sm+): ReactFlow canvas. Hidden on mobile via CSS — still in the DOM;
          perf impact is acceptable given the medium severity of the mobile issue. */}
      <div className="hidden sm:block h-[440px] overflow-hidden rounded-lg border border-zinc-200 bg-white md:h-[540px] dark:border-zinc-800 dark:bg-zinc-950">
        <ReactFlow<GitCommitFlowNode, Edge>
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 1.1 }}
          minZoom={0.15}
          maxZoom={1.8}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          onNodeClick={(_event, node) => onSelectSha(node.id)}
          proOptions={{ hideAttribution: true }}
          className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100 [&_.react-flow__controls]:overflow-hidden [&_.react-flow__controls]:rounded-lg [&_.react-flow__controls]:border [&_.react-flow__controls]:border-zinc-200 [&_.react-flow__controls]:bg-white dark:[&_.react-flow__controls]:border-zinc-700 dark:[&_.react-flow__controls]:bg-zinc-900 [&_.react-flow__controls-button]:border-zinc-200 [&_.react-flow__controls-button]:bg-white [&_.react-flow__controls-button]:text-zinc-950 [&_.react-flow__controls-button]:transition-colors [&_.react-flow__controls-button:hover]:bg-zinc-100 dark:[&_.react-flow__controls-button]:border-zinc-700 dark:[&_.react-flow__controls-button]:bg-zinc-900 dark:[&_.react-flow__controls-button]:text-zinc-100 dark:[&_.react-flow__controls-button:hover]:bg-zinc-800 [&_.react-flow__controls-button_svg]:fill-current [&_.react-flow__minimap]:rounded-lg [&_.react-flow__minimap]:border [&_.react-flow__minimap]:border-zinc-200 [&_.react-flow__minimap]:bg-white/95 dark:[&_.react-flow__minimap]:border-zinc-700 dark:[&_.react-flow__minimap]:bg-zinc-900/95"
        >
          <MiniMap
            pannable
            zoomable
            bgColor="hsl(var(--card))"
            nodeStrokeWidth={3}
            nodeColor={(node) => laneColor((node.data as unknown as GitCommitNodeData).lane)}
            nodeStrokeColor="hsl(var(--border))"
            maskColor="hsl(var(--background) / 0.55)"
          />
          <Controls showInteractive={false} />
          <Background gap={24} size={1} color="rgb(148 163 184 / 0.2)" />
        </ReactFlow>
      </div>
    </>
  )
}
